import { Injectable } from '@angular/core';

/**
 * AppBridge interface - wrapper for Bullhorn API communication
 * This is a local implementation to replace the novo-elements dependency
 */
export interface IAppBridge {
  httpGET(url: string, timeout?: number): Promise<any>;
  httpPOST(url: string, data: any): Promise<any>;
  httpPUT(url: string, data: any): Promise<any>;
}

/**
 * Local AppBridgeService implementation
 * Provides cross-iframe communication with Bullhorn using post-robot
 */
@Injectable({
  providedIn: 'root'
})
export class AppBridgeService {
  private postRobot: any;

  constructor() {
    // Dynamically import post-robot if available
    if (typeof window !== 'undefined' && (window as any).postRobot) {
      this.postRobot = (window as any).postRobot;
    }
  }

  /**
   * Creates an AppBridge instance for the specified application
   * @param appName Name of the application (e.g., 'ai-assist-ui')
   * @returns AppBridge instance
   */
  create(appName: string): IAppBridge {
    return new AppBridgeImpl(appName, this.postRobot);
  }
}

/**
 * AppBridge implementation using post-robot for parent frame communication
 */
class AppBridgeImpl implements IAppBridge {
  constructor(
    private appName: string,
    private postRobot: any
  ) {
    console.debug(`AppBridge initialized for ${appName}`);
  }

  async httpGET(relativeURL: string, timeout: number = 60000): Promise<any> {
    if (!this.postRobot) {
      throw new Error('post-robot is not available. Ensure the app is running within Bullhorn.');
    }

    try {
      const response = await this.postRobot.sendToParent(
        'httpGET',
        {
          relativeURL,
          origin: undefined,
          originTraceName: this.appName
        },
        { timeout }
      );
      return { data: response.data.data, error: response.data.error };
    } catch (error) {
      console.error(`AppBridge httpGET error:`, error);
      throw error;
    }
  }

  async httpPOST(relativeURL: string, postData: any): Promise<any> {
    if (!this.postRobot) {
      throw new Error('post-robot is not available. Ensure the app is running within Bullhorn.');
    }

    try {
      const response = await this.postRobot.sendToParent(
        'httpPOST',
        {
          relativeURL,
          data: postData,
          origin: undefined,
          originTraceName: this.appName
        },
        { timeout: 60000 }
      );
      return { data: response.data.data, error: response.data.error };
    } catch (error) {
      console.error(`AppBridge httpPOST error:`, error);
      throw error;
    }
  }

  async httpPUT(relativeURL: string, putData: any): Promise<any> {
    if (!this.postRobot) {
      throw new Error('post-robot is not available. Ensure the app is running within Bullhorn.');
    }

    try {
      const response = await this.postRobot.sendToParent(
        'httpPUT',
        {
          relativeURL,
          data: putData,
          origin: undefined,
          originTraceName: this.appName
        },
        { timeout: 60000 }
      );
      return { data: response.data.data, error: response.data.error };
    } catch (error) {
      console.error(`AppBridge httpPUT error:`, error);
      throw error;
    }
  }
}
